import { Environment, LogEvent, LogLevel } from '@core/types';

import { sendSeqEvents } from './api';
import { Enricher, ILogger, LogDeviceInformation } from './types';

export class Log implements ILogger {
    private processing = false;
    private queue: string[] = [];

    private verboseEnabled = true;
    private debugEnabled = true;
    private informationEnabled = true;
    private warningEnabled = true;
    private errorEnabled = true;
    private fatalEnabled = true;
    private resolve = false;
    private seqUrl: string;
    private logRequestEnrich: (clone: any) => void;

    private enrichObject: Enricher = {
        environment: Environment.DEVELOPMENT
    };

    constructor(seqUrl: string, isProduction: boolean, logDeviceInformation: LogDeviceInformation, logRequestEnrich: (clone: any) => void) {
        this.seqUrl = seqUrl;
        this.enrichObject.deviceInformation = logDeviceInformation;
        this.logRequestEnrich = logRequestEnrich;

        if (!isProduction) {
            this.resolve = true;
            this.enrichObject.environment = Environment.STAGING;
        } else {
            this.resolve = false;
            this.enrichObject.environment = Environment.PRODUCTION;
        }

        this.enrich(this.enrichObject);
    }

    private log = (level: LogLevel, message: string, parameters?: any): void => {
        const clone: any = {};

        let property: string;
        if (parameters) {
            for (property in parameters) {
                if (Object.prototype.hasOwnProperty.call(parameters, property)) {
                    clone[property] = parameters[property];
                }
            }
        }

        for (property in this.enrichObject) {
            if (Object.prototype.hasOwnProperty.call(this.enrichObject, property)) {
                clone[property] = (this.enrichObject as any)[property];
            }
        }
        this.logRequestEnrich(clone);

        const event: LogEvent = {
            Timestamp: this.dateToString(new Date()),
            Level: LogLevel[level],
            MessageTemplate: message,
            Properties: clone
        };
        try {
            const json: string = JSON.stringify(event);
            this.queue.push(json);
            this.processQueue();
        } catch (e) {
            console.error('Error stringifying log event', event, e);
        }

        if ((!this.resolve && level < LogLevel.Error) || !message) {
            return;
        }

        let output = message;
        for (property in clone) {
            if (Object.prototype.hasOwnProperty.call(clone, property)) {
                try {
                    output = output.replace(new RegExp(`{@${property}}`, 'g'), JSON.stringify(clone[property]));
                    output = output.replace(new RegExp(`{${property}}`, 'g'), clone[property]);
                } catch (e) {
                    console.error('Error replacing output logging', parameters, e);
                }
            }
        }
        switch (level) {
            case LogLevel.Verbose:
                console.trace(output, clone);
                break;
            case LogLevel.Debug:
                console.debug(output, clone);
                break;
            case LogLevel.Warning:
                console.warn(output, clone);
                break;
            case LogLevel.Error:
            case LogLevel.Fatal:
                console.error(output, clone);
                break;
            case LogLevel.Information:
                console.info(output, clone);
                break;
            default:
                console.log(output, clone);
                break;
        }
    };

    public verbose = (message: string, parameters?: any): void => {
        if (!this.verboseEnabled) {
            return;
        }
        this.log(LogLevel.Verbose, message, parameters);
    };

    public debug = (message: string, parameters?: any): void => {
        if (!this.debugEnabled) {
            return;
        }
        this.log(LogLevel.Debug, message, parameters);
    };

    public information = (message: string, parameters?: any): void => {
        if (!this.informationEnabled) {
            return;
        }
        this.log(LogLevel.Information, message, parameters);
    };

    public warning = (message: string, parameters?: any): void => {
        if (!this.warningEnabled) {
            return;
        }
        this.log(LogLevel.Warning, message, parameters);
    };

    public error = (message: string, parameters?: any): void => {
        if (!this.errorEnabled) {
            return;
        }
        this.log(LogLevel.Error, message, parameters);
    };

    public fatal = (message: string, parameters?: any): void => {
        if (!this.fatalEnabled) {
            return;
        }
        this.log(LogLevel.Fatal, message, parameters);
    };

    private dateToString = (d: Date): string => {
        const year: string = d.getFullYear().toString();
        let month: string = (d.getMonth() + 1).toString();
        if (month.length === 1) {
            month = `0${month}`;
        }
        let day: string = d.getDate().toString();
        if (day.length === 1) {
            day = `0${day}`;
        }
        let hours: string = d.getHours().toString();
        if (hours.length === 1) {
            hours = `0${hours}`;
        }
        let minutes: string = d.getMinutes().toString();
        if (minutes.length === 1) {
            minutes = `0${minutes}`;
        }
        let seconds: string = d.getSeconds().toString();
        if (seconds.length === 1) {
            seconds = `0${seconds}`;
        }
        let milliseconds: string = d.getMilliseconds().toString();
        if (milliseconds.length === 1) {
            milliseconds = `0${milliseconds}`;
        }
        if (milliseconds.length === 2) {
            milliseconds = `0${milliseconds}`;
        }
        // HACK: There are some timezones with 30 minutes, this hacks them off
        let timezone: string = Math.floor(Math.abs(d.getTimezoneOffset() / 60)).toString();
        if (timezone.length === 1) {
            timezone = `0${timezone}`;
        }
        if (d.getTimezoneOffset() < 0) {
            timezone = `+${timezone}`;
        } else {
            timezone = `-${timezone}`;
        }
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezone}00`;
    };

    private processQueue = (): void => {
        if (this.processing || this.queue.length === 0) {
            return;
        }
        this.processing = true;

        let data = '{ "Events": [';
        data += this.queue.join(',');
        data += '] }';

        this.queue.length = 0;

        sendSeqEvents(this.seqUrl, data)
            .then(() => {
                this.processing = false;
                this.processQueue();
            })
            .catch((err) => {
                console.log(err);
                this.processing = false;
                this.processQueue();
            });
    };

    private enrich = (enrichObject: any): void => {
        for (const property in enrichObject) {
            if (Object.prototype.hasOwnProperty.call(enrichObject, property)) {
                (this.enrichObject as any)[property] = enrichObject[property];
            }
        }
    };
}
